import { useState, useEffect, useLayoutEffect, Suspense } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import "@/App.css";
import "@/bookingflows-theme.css";
import "@/dark-theme.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { lazyRetry } from "./utils/lazyRetry";
import { ConnectRoute } from "./components/connect/ConnectShell";
import { IntelRoute } from "./components/intelligence/IntelRoute";
import ErrorBoundary from "./components/ErrorBoundary";
const FindBookingPage = lazyRetry(() => import("./pages/FindBookingPage"));
const BookingConfirmedPage = lazyRetry(() => import("./pages/BookingConfirmedPage"));
const QuickBooksDisconnectedPage = lazyRetry(() => import("./pages/QuickBooksDisconnectedPage"));
const QuickBooksLaunchPage = lazyRetry(() => import("./pages/QuickBooksLaunchPage"));
const SecurityPage = lazyRetry(() => import("./pages/SecurityPage"));
const StandaloneWaiverPage = lazyRetry(() => import("./pages/StandaloneWaiverPage"));
const KioskPage = lazyRetry(() => import("./pages/KioskPage"));
const MarketplaceLanding = lazyRetry(() => import("./pages/MarketplaceLanding"));
const MarketplaceSearch = lazyRetry(() => import("./pages/MarketplaceSearch"));
const ExperienceDetail = lazyRetry(() => import("./pages/ExperienceDetail"));
const CartContinuePage = lazyRetry(() => import("./pages/CartContinuePage"));
const OperatorProfile = lazyRetry(() => import("./pages/OperatorProfile"));
const SupplierLandingPage = lazyRetry(() => import("./pages/SupplierLandingPage"));
const SupplierFeaturesPage = lazyRetry(() => import("./pages/SupplierFeaturesPage"));
const CityLandingPage = lazyRetry(() => import("./pages/CityLandingPage"));
const BlogListingPage = lazyRetry(() => import("./pages/BlogListingPage"));
const BlogSlugRouter = lazyRetry(() => import("./pages/BlogSlugRouter"));
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"));
const DeleteAccountPage = lazyRetry(() => import("./pages/DeleteAccountPage"));
const SalesDeckPage = lazyRetry(() => import("./pages/SalesDeckPage"));
const TermsOfService = lazyRetry(() => import("./pages/TermsOfService"));
const DmcaPolicy = lazyRetry(() => import("./pages/DmcaPolicy"));
const SecurityPolicyPage = lazyRetry(() => import("./pages/SecurityPolicyPage"));
const VulnerabilityDisclosure = lazyRetry(() => import("./pages/VulnerabilityDisclosure"));
const CookiePolicy = lazyRetry(() => import("./pages/CookiePolicy"));
const DataProcessingAgreement = lazyRetry(() => import("./pages/DataProcessingAgreement"));
const AcceptableUsePolicy = lazyRetry(() => import("./pages/AcceptableUsePolicy"));
const GiftCardPurchasePage = lazyRetry(() => import("./pages/GiftCardPurchasePage"));
const GiftCardViewPage = lazyRetry(() => import("./pages/GiftCardViewPage"));
const GiftCardLookupPage = lazyRetry(() => import("./pages/GiftCardLookupPage"));
const GiftCardThankYouPage = lazyRetry(() => import("./pages/GiftCardThankYouPage"));
const CaseStudyBOATX = lazyRetry(() => import("./pages/CaseStudyBOATX"));
const MeetTheTeam = lazyRetry(() => import("./pages/MeetTheTeam"));
const CaseStudyBananaBoat = lazyRetry(() => import("./pages/CaseStudyBananaBoat"));
const CaseStudyFunBeach = lazyRetry(() => import("./pages/CaseStudyFunBeach"));
const CaseStudyTrailBlazer = lazyRetry(() => import("./pages/CaseStudyTrailBlazer"));
const ReferralProgramPage = lazyRetry(() => import("./pages/ReferralProgramPage"));
const PackageConfirmPage = lazyRetry(() => import("./pages/PackageConfirmPage"));
const MarketplacePackagePage = lazyRetry(() => import("./pages/MarketplacePackagePage"));
const ClubEventsPage = lazyRetry(() => import("./pages/ClubEventsPage"));
const MemberArenaPage = lazyRetry(() => import("./pages/MemberArenaPage"));
const ReferralTermsPage = lazyRetry(() => import("./pages/ReferralTermsPage"));
const OnboardingGuide = lazyRetry(() => import("./pages/OnboardingGuide"));
const DemoSignup = lazyRetry(() => import("./pages/DemoSignup"));
const HelpCenterPage = lazyRetry(() => import("./pages/HelpCenterPage"));
const ApiDocsPage = lazyRetry(() => import("./pages/ApiDocsPage"));
const DesignSystemPage = lazyRetry(() => import("./pages/DesignSystemPage"));
const ObservatoryLogin = lazyRetry(() => import("./pages/observatory/ObservatoryLogin"));
const ObservatoryPage = lazyRetry(() => import("./pages/observatory/ObservatoryPage"));
const ZiinaOAuthCallback = lazyRetry(() => import("./pages/ZiinaOAuthCallback"));
const SignupV3 = lazyRetry(() => import("./pages/SignupV3"));
const SignupV2 = lazyRetry(() => import("./pages/SignupV2"));
const ForgotShortname = lazyRetry(() => import("./pages/ForgotShortname"));
const ForgotPassword = lazyRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const VerifyTeamMember = lazyRetry(() => import("./pages/VerifyTeamMember"));
const SetTeamMemberPassword = lazyRetry(() => import("./pages/SetTeamMemberPassword"));
const ResetTeamPassword = lazyRetry(() => import("./pages/ResetTeamPassword"));
const VerifyEmailChange = lazyRetry(() => import("./pages/VerifyEmailChange"));
const RevertEmailChange = lazyRetry(() => import("./pages/RevertEmailChange"));
const LoginV2 = lazyRetry(() => import("./pages/LoginV2"));
const Login = lazyRetry(() => import("./pages/Login"));
const PlatformAdminLogin = lazyRetry(() => import("./pages/PlatformAdminLogin"));
const Dashboard = lazyRetry(() => import("./pages/DashboardV2"));
const TeamMemberViewV2 = lazyRetry(() => import("./pages/TeamMemberViewV2"));
const TeamMemberSignup = lazyRetry(() => import("./pages/TeamMemberSignup"));
const TeamMemberLogin = lazyRetry(() => import("./pages/TeamMemberLogin"));
const TeamMemberDashboard = lazyRetry(() => import("./pages/TeamMemberDashboard"));
const SuperAdminDashboard = lazyRetry(() => import("./pages/SuperAdminDashboard"));
const SuperAdminDashboardV2 = lazyRetry(() => import("./pages/SuperAdminDashboardV2"));
const ProfilePage = lazyRetry(() => import("./pages/ProfilePage"));
const ForCharters = lazyRetry(() => import("./pages/ForCharters"));
const ForTours = lazyRetry(() => import("./pages/ForTours"));
const ForRentals = lazyRetry(() => import("./pages/ForRentals"));
const SettingsPage = lazyRetry(() => import("./pages/SettingsPageNew"));
const OrgSettingsPage = lazyRetry(() => import("./pages/OrgSettingsPage"));
const AddTeamFlow = lazyRetry(() => import("./pages/AddTeamFlow"));
const AddAssetFlow = lazyRetry(() => import("./pages/AddAssetFlow"));
const TeamLoginMinimal = lazyRetry(() => import("./pages/TeamLoginMinimal"));
const CustomerTicketPage = lazyRetry(() => import("./pages/CustomerTicketPage"));
const InvoicePage = lazyRetry(() => import("./pages/InvoicePage"));
const SubscriptionManagePage = lazyRetry(() => import("./pages/SubscriptionManagePage"));
const BrandPage = lazyRetry(() => import("./pages/BrandPage"));
const CustomerBookingWidget = lazyRetry(() => import("./pages/CustomerBookingWidget"));
const IntelligenceHome = lazyRetry(() => import("./pages/IntelligenceHome"));
const AssetEarningsReport = lazyRetry(() => import("./pages/AssetEarningsReport"));
const ExpensesReport = lazyRetry(() => import("./pages/ExpensesReport"));
const ServiceSalesMixReport = lazyRetry(() => import("./pages/ServiceSalesMixReport"));
const DemandCalendarReport = lazyRetry(() => import("./pages/DemandCalendarReport"));
const CustomerInsightsReport = lazyRetry(() => import("./pages/CustomerInsightsReport"));
const BookingFunnelReport = lazyRetry(() => import("./pages/BookingFunnelReport"));
const WebsiteAnalyticsReport = lazyRetry(() => import("./pages/WebsiteAnalyticsReport"));
const AudiencesReport = lazyRetry(() => import("./pages/AudiencesReport"));
const CampaignsReport = lazyRetry(() => import("./pages/CampaignsReport"));
const ConnectMarketplace = lazyRetry(() => import("./pages/connect/ConnectMarketplace"));
const ConnectFlows = lazyRetry(() => import("./pages/connect/ConnectFlows"));
const ConnectEarnings = lazyRetry(() => import("./pages/connect/ConnectEarnings"));
const ConnectListings = lazyRetry(() => import("./pages/connect/ConnectListings"));
const ConnectStorefront = lazyRetry(() => import("./pages/ConnectStorefront"));
const BookingCancelled = lazyRetry(() => import("./pages/BookingCancelled"));
const PaymentCancelled = lazyRetry(() => import("./pages/PaymentCancelled"));
const PartnerPortalPage = lazyRetry(() => import("./pages/PartnerPortalPage"));
const BookingPage = lazyRetry(() => import("./pages/BookingPage"));
const FlowBookingPage = lazyRetry(() => import("./pages/FlowBookingPage"));
const CheckInPage = lazyRetry(() => import("./pages/CheckInPage"));
const QRScannerGun = lazyRetry(() => import("./pages/QRScannerGun"));
const SelfCheckIn = lazyRetry(() => import("./pages/SelfCheckIn"));
const VerifyEmail = lazyRetry(() => import("./pages/VerifyEmail"));
const PendingApproval = lazyRetry(() => import("./pages/PendingApproval"));
const NotFoundPage = lazyRetry(() => import("./pages/NotFoundPage"));
const MyTrips = lazyRetry(() => import("./pages/MyTrips"));
const BookingDetail = lazyRetry(() => import("./pages/BookingDetail"));
const CustomerAuthCallback = lazyRetry(() => import("./pages/CustomerAuthCallback"));
const CustomerReviewPage = lazyRetry(() => import("./pages/CustomerReviewPage"));
const ReviewRequestPage = lazyRetry(() => import("./pages/ReviewRequestPage"));
const MagicLinkCallback = lazyRetry(() => import("./pages/MagicLinkCallback"));
const SetPasswordPage = lazyRetry(() => import("./pages/SetPasswordPage"));
import LiveViewersBadge from "./components/LiveViewersBadge"; // Live marketplace viewers + BF pixel
import { Toaster } from "./components/ui/sonner";
import { SettingsProvider } from "./contexts/SettingsContext";
import { CustomerAuthProvider } from "./contexts/CustomerAuthContext"; // Customer auth context
import CookieConsent, { applyExistingConsent } from "./components/CookieConsent";

// Disable browser scroll restoration globally — we handle it manually
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// ScrollToTop component - resets scroll position on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  // useLayoutEffect fires before paint, preventing any scroll flash
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    // The real scroll container is `.App` (height:100% + overflow-y:auto); #root
    // wraps it. Reset both — otherwise navigating between pages keeps prior scroll.
    const root = document.getElementById('root');
    if (root) root.scrollTop = 0;
    const appEl = document.querySelector('.App');
    if (appEl) appEl.scrollTop = 0;
  }, [pathname]);

  return null;
}

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="page-loading">
    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

// "/" never shows a spinner: index.html paints a static hero shell before any JS
// (window.__BF_SHELL__), and this fallback re-renders that exact markup while
// the landing chunk loads — content stays on screen from first byte to hydration.
const LandingShellFallback = () => (
  <div
    data-testid="landing-shell-fallback"
    dangerouslySetInnerHTML={{ __html: (typeof window !== "undefined" && window.__BF_SHELL__) || "" }}
  />
);

// Capacitor plugins for iOS/Android native app
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import NativeSplash from './components/NativeSplash';
import { Keyboard } from '@capacitor/keyboard';
import { initPushNotifications, registerForPushNotifications } from './utils/pushNotifications';
import { initSessionKeepAlive } from './utils/sessionRefresh';
import { initExternalLinkHandler } from './utils/externalLinks';
import { hapticSuccess } from './utils/haptics';

// Check if running as native app
const isNativeApp = Capacitor.isNativePlatform();

// Initialize Capacitor plugins for native apps
const initializeCapacitor = async () => {
  if (isNativeApp) {
    try {
      // Configure status bar - dark icons on white to match the app header
      await StatusBar.setStyle({ style: Style.Light });
      
      // On Android: white bar, no webview underlap
      if (Capacitor.getPlatform() === 'android') {
        await StatusBar.setBackgroundColor({ color: '#FFFFFF' });
        await StatusBar.setOverlaysWebView({ overlay: false });
      }
      
      // Keep the focused input visible above the keyboard
      Keyboard.addListener('keyboardDidShow', () => {
        const el = document.activeElement;
        if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      });
      
      // Initialize push notification listeners
      initPushNotifications({
        onRegistration: (token) => {
          // Token will be sent to backend when user logs in
          localStorage.setItem('pushToken', token);
        },
        onNotificationReceived: (notification) => {
          // Haptic feedback when notification arrives
          hapticSuccess();
        },
        onNotificationAction: (action) => {
          // Handle navigation based on notification data
          const data = action.notification.data;
          if (data?.type === 'new_booking' && data?.bookingId) {
            // Navigate to booking details - handled by the app's routing
            window.location.href = `/dashboard?booking=${data.bookingId}`;
          }
        },
      });
      
      // Request push notification permissions
      await registerForPushNotifications();
      
      // Native splash hide is handled by <NativeSplash /> (animated takeover)
    } catch (error) {
    }
  }
};

// Apply existing cookie consent on app load (for analytics, etc.) - ONLY on web
if (!isNativeApp) {
  applyExistingConsent();
}

// const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY; // Temporarily disabled

// Protected Route Component with proper authentication state handling
const ProtectedRoute = ({ children, requiredType }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = loading, true/false = checked
  const [userType, setUserType] = useState(null);
  
  useEffect(() => {
    // Small delay to allow localStorage to persist after login
    const checkAuth = () => {
      const storedUserType = localStorage.getItem("userType");
      const token = localStorage.getItem("jwtToken"); // Fixed: was "token", should be "jwtToken"
      
      
      if (token && storedUserType) {
        setUserType(storedUserType);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    };
    
    // Check immediately and after a small delay to handle race condition
    checkAuth();
    const timeoutId = setTimeout(checkAuth, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);
  
  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return null;
  }
  
  if (!isAuthenticated) {
    // Redirect super_admin to platform-admin login (uses PostgreSQL v2)
    if (requiredType === 'super_admin') {
      return <Navigate to="/platform-admin" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  
  if (requiredType && userType !== requiredType) {
    // Redirect to appropriate login based on required type
    if (requiredType === 'super_admin') {
      return <Navigate to="/platform-admin" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Team Protected Route Component for teamMember authentication
const TeamProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  
  useEffect(() => {
    const checkAuth = () => {
      const teamToken = localStorage.getItem("teamToken");
      const teamData = localStorage.getItem("teamData");
      
      if (teamToken && teamData) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    };
    
    checkAuth();
    const timeoutId = setTimeout(checkAuth, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);
  
  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return null;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/team/login" replace />;
  }
  
  return children;
};

// Component to handle native app startup - ensures app starts on marketplace
const NativeAppStartupHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (isNativeApp) {
      const hasLaunchedBefore = sessionStorage.getItem('nativeAppLaunched');
      
      if (!hasLaunchedBefore) {
        sessionStorage.setItem('nativeAppLaunched', 'true');
        // Native app starts on /login for operators (Apple requirement)
        if (location.pathname === '/' || location.pathname === '') {
          navigate('/login', { replace: true });
        }
      }
    }
  }, [navigate, location.pathname]);
  
  return null;
};

function App() {
  // Temporarily disable Clerk to debug
  // if (!CLERK_PUBLISHABLE_KEY) {
  //   console.warn('Clerk publishable key not found - some features may not work');
  // }

  // Initialize Capacitor for native iOS/Android app
  useEffect(() => {
    initializeCapacitor();
    initSessionKeepAlive();
    initExternalLinkHandler();
  }, []);

  return (
    // <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} afterSignOutUrl="/login">
    <HelmetProvider>
      {/* Default SEO tags - pages can override these */}
      <Helmet>
        <title>Booking Software for Tours, Charters &amp; Rentals | BookingFlows</title>
        <meta name="description" content="Online booking software for tour operators, charter companies &amp; rental businesses. Accept bookings 24/7, take payments &amp; manage your team. Free to start." />
        <meta name="keywords" content="booking software, online booking system, tour booking software, charter booking software, rental booking software, activity booking software, reservation system" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Booking Software for Tours &amp; Experiences | BookingFlows" />
        <meta property="og:description" content="Take bookings online. Get paid automatically. Manage your team. Built for tours, charters &amp; rentals." />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <SettingsProvider>
        <CustomerAuthProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            {/* Animated splash takeover (native apps; preview with ?splashPreview=1) */}
            <NativeSplash />
            {/* Handle native app startup - redirect to marketplace */}
            {isNativeApp && <NativeAppStartupHandler />}
            {/* Real live-viewer badge on marketplace + landing (also fires the BF pixel) */}
            <LiveViewersBadge />
            <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* Marketplace routes (customer-facing) */}
              <Route path="/" element={<Suspense fallback={<LandingShellFallback />}><SupplierLandingPage /></Suspense>} />
              <Route path="/for-suppliers" element={<Suspense fallback={<LandingShellFallback />}><SupplierLandingPage /></Suspense>} />
              <Route path="/features" element={<SupplierFeaturesPage />} />
              <Route path="/marketplace" element={<MarketplaceLanding />} />
              <Route path="/search" element={<MarketplaceSearch />} />
              <Route path="/cart/continue" element={<CartContinuePage />} />
              <Route path="/package/confirm" element={<PackageConfirmPage />} />
              <Route path="/package/:packageId" element={<MarketplacePackagePage />} />
              <Route path="/marketplace/search" element={<MarketplaceSearch />} />
              <Route path="/experience/:serviceId" element={<ExperienceDetail />} />
              <Route path="/b/:orgSlug" element={<BrandPage />} /> {/* Hosted brand page */}
              <Route path="/b/:orgSlug/events" element={<ClubEventsPage />} /> {/* Public event calendar */}
              <Route path="/members/:orgSlug" element={<MemberArenaPage />} /> {/* Members Arena (customer-gated) */}
              <Route path="/b/:orgSlug/e/:serviceId" element={<ExperienceDetail />} /> {/* Brand-scoped detail */}
              <Route path="/host/:operatorId" element={<OperatorProfile />} />
              <Route path="/for-suppliers" element={<SupplierLandingPage />} />
              <Route path="/tours-in/:citySlug" element={<CityLandingPage />} />
              {/* Gift Cards (Phase 4) — view page must come BEFORE :orgSlug to avoid shadow */}
              <Route path="/gift-card/view/:viewToken" element={<GiftCardViewPage />} />
              <Route path="/gift-card/lookup" element={<GiftCardLookupPage />} />
              <Route path="/gift-card/thank-you" element={<GiftCardThankYouPage />} />
              <Route path="/gift-card/:orgSlug" element={<GiftCardPurchasePage />} />
              {/* Industry landing pages — lazy loaded, used for Google Ads */}
              <Route path="/for-charters" element={<ForCharters />} />
              <Route path="/for-tours"    element={<ForTours />} />
              <Route path="/for-rentals"  element={<ForRentals />} />
              <Route path="/find-booking" element={<FindBookingPage />} />
              <Route path="/quickbooks-disconnected" element={<QuickBooksDisconnectedPage />} />
              <Route path="/quickbooks/launch" element={<QuickBooksLaunchPage />} />
              <Route path="/quickbooks/connect" element={<QuickBooksLaunchPage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/support" element={<HelpCenterPage />} />
              <Route path="/booking-confirmed" element={<BookingConfirmedPage />} />
              {/* Customer case studies — sales weapon for the team */}
              <Route path="/customers/boatx" element={<CaseStudyBOATX />} />
              <Route path="/meet-the-team" element={<MeetTheTeam />} />
              <Route path="/customers/banana-boat-rentals" element={<CaseStudyBananaBoat />} />
              <Route path="/customers/fun-beach-water-sports" element={<CaseStudyFunBeach />} />
              <Route path="/customers/trail-blazer-yachts" element={<CaseStudyTrailBlazer />} />
              <Route path="/referral-program" element={<ReferralProgramPage />} />
              <Route path="/referral-terms" element={<ReferralTermsPage />} />
              <Route path="/onboarding" element={<OnboardingGuide />} />
              <Route path="/demo" element={<DemoSignup />} />
              <Route path="/sign/:orgSlug" element={<StandaloneWaiverPage />} />
              <Route path="/kiosk/:orgSlug" element={<KioskPage />} />
              <Route path="/blog" element={<BlogListingPage />} />
              <Route path="/blog/:slug" element={<BlogSlugRouter />} />
              <Route path="/developers" element={<ApiDocsPage />} />
              <Route path="/design-system" element={<DesignSystemPage />} />
              {/* UAE Observatory — regulator-facing aggregated analytics (own auth) */}
              <Route path="/observatory" element={<ObservatoryLogin />} />
              <Route path="/observatory/dashboard" element={<ErrorBoundary name="Observatory"><ObservatoryPage /></ErrorBoundary>} />
              
              {/* Customer profile routes */}
              <Route path="/my-trips" element={<MyTrips />} />
              <Route path="/my-trips/callback" element={<CustomerAuthCallback />} />
              <Route path="/my-trips/:bookingId" element={<BookingDetail />} />
              <Route path="/auth/magic-link" element={<MagicLinkCallback />} />
              <Route path="/auth/set-password" element={<SetPasswordPage />} />
              
              {/* Public routes - Native app starts on login, web starts on landing */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route path="/deck" element={<SalesDeckPage />} />
              <Route path="/dmca" element={<DmcaPolicy />} />
              <Route path="/security-policy" element={<SecurityPolicyPage />} />
              <Route path="/vulnerability-disclosure" element={<VulnerabilityDisclosure />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/dpa" element={<DataProcessingAgreement />} />
              <Route path="/acceptable-use" element={<AcceptableUsePolicy />} />
              <Route path="/beacon/privacy" element={<Navigate to="/privacy" replace />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/customer-ticket/:ticketId" element={<CustomerTicketPage />} />
              <Route path="/ticket/:ticketId" element={<CustomerTicketPage />} /> {/* Alias for magic links */}
              <Route path="/:orgSlug/ticket/:ticketId" element={<CustomerTicketPage />} /> {/* Org-prefixed ticket URL */}
              <Route path="/invoice/:invoiceNumber" element={<InvoicePage />} /> {/* Quick Invoice receipt */}
              <Route path="/subscription/:token" element={<SubscriptionManagePage />} /> {/* Subscription manage */}
              <Route path="/ziina/callback" element={<ZiinaOAuthCallback />} /> {/* Ziina OAuth redirect URI */}
              <Route path="/review/:token" element={<ReviewRequestPage />} /> {/* Magic-link post-trip review (no login) */}
              <Route path="/m/:orgSlug/review" element={<CustomerReviewPage />} /> {/* Customer review page with /m/ prefix */}
              <Route path="/:orgSlug/review" element={<CustomerReviewPage />} /> {/* Customer review page */}
              <Route path="/check-in/:confirmationCode" element={<CheckInPage />} />
              <Route path="/self-check-in" element={<SelfCheckIn />} /> {/* Customer self check-in portal */}
              <Route path="/qr-code-scanner" element={<QRScannerGun />} /> {/* QR Scanner Gun for staff */}
              <Route path="/book/:orgSlug/:flowSlug" element={<ErrorBoundary name="FlowBooking"><FlowBookingPage /></ErrorBoundary>} />
              <Route path="/book" element={<ErrorBoundary name="BookingWidget"><CustomerBookingWidget /></ErrorBoundary>} />
              <Route path="/booking" element={<BookingPage />} />
              <Route path="/booking-cancelled" element={<BookingCancelled />} />
              <Route path="/payment-cancelled" element={<PaymentCancelled />} />
              <Route path="/signup" element={<SignupV3 />} />
              <Route path="/signup-v2" element={<SignupV2 />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/pending-approval" element={<PendingApproval />} />
              <Route path="/forgot-shortname" element={<ForgotShortname />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-team-member" element={<VerifyTeamMember />} />
              <Route path="/set-team-password" element={<SetTeamMemberPassword />} />
              <Route path="/reset-team-password" element={<ResetTeamPassword />} />
              <Route path="/verify-email-change" element={<VerifyEmailChange />} />
              <Route path="/revert-email-change" element={<RevertEmailChange />} />
              <Route path="/login" element={<LoginV2 />} />
              <Route path="/login-legacy" element={<Login />} />
              <Route path="/platform-admin" element={<PlatformAdminLogin />} />
              <Route path="/teamMember-signup" element={<TeamMemberSignup />} />
              <Route path="/teamMember/login" element={<TeamMemberLogin />} />
              <Route path="/teamMember/dashboard" element={<TeamMemberDashboard />} />
              
              {/* Team member routes - TESTING WITH MINIMAL COMPONENT */}
              <Route path="/team/login" element={<TeamLoginMinimal />} />
              <Route path="/team/add" element={
                <ProtectedRoute>
                  <AddTeamFlow />
                </ProtectedRoute>
              } />
              <Route path="/asset/add" element={
                <ProtectedRoute>
                  <AddAssetFlow />
                </ProtectedRoute>
              } />
              
              {/* Protected routes */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute>
                    <ErrorBoundary name="Dashboard">
                      <Dashboard />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/team-member" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/intelligence" 
                element={
                  <IntelRoute tab="home">
                    <ErrorBoundary name="Intelligence">
                      <IntelligenceHome />
                    </ErrorBoundary>
                  </IntelRoute>
                } 
              />
              {/* 📡 BookingFlows Connect — the affiliate network arena (everyone with an account) */}
              <Route path="/connect" element={<ConnectRoute><ErrorBoundary name="ConnectMarketplace"><ConnectMarketplace /></ErrorBoundary></ConnectRoute>} />
              <Route path="/connect/flows" element={<ConnectRoute><ErrorBoundary name="ConnectFlows"><ConnectFlows /></ErrorBoundary></ConnectRoute>} />
              <Route path="/connect/earnings" element={<ConnectRoute><ErrorBoundary name="ConnectEarnings"><ConnectEarnings /></ErrorBoundary></ConnectRoute>} />
              <Route path="/connect/listings" element={<ConnectRoute><ErrorBoundary name="ConnectListings"><ConnectListings /></ErrorBoundary></ConnectRoute>} />
              {/* Public flow storefront — behind every Connect direct URL / embed */}
              <Route path="/c/:flowSlug" element={<ErrorBoundary name="ConnectStorefront"><ConnectStorefront /></ErrorBoundary>} />
              <Route 
                path="/intelligence/asset-earnings" 
                element={
                  <IntelRoute tab="asset-earnings">
                    <ErrorBoundary name="AssetEarningsReport">
                      <AssetEarningsReport />
                    </ErrorBoundary>
                  </IntelRoute>
                } 
              />
              <Route 
                path="/intelligence/service-sales-mix" 
                element={
                  <IntelRoute tab="service-sales-mix">
                    <ErrorBoundary name="ServiceSalesMixReport">
                      <ServiceSalesMixReport />
                    </ErrorBoundary>
                  </IntelRoute>
                } 
              />
              <Route 
                path="/intelligence/demand-calendar" 
                element={
                  <IntelRoute tab="demand-calendar">
                    <ErrorBoundary name="DemandCalendarReport">
                      <DemandCalendarReport />
                    </ErrorBoundary>
                  </IntelRoute>
                } 
              />
              <Route 
                path="/intelligence/customer-insights" 
                element={
                  <IntelRoute tab="customer-insights">
                    <ErrorBoundary name="CustomerInsightsReport">
                      <CustomerInsightsReport />
                    </ErrorBoundary>
                  </IntelRoute>
                } 
              />
              <Route
                path="/intelligence/expenses"
                element={
                  <IntelRoute tab="expenses">
                    <ErrorBoundary name="ExpensesReport">
                      <ExpensesReport />
                    </ErrorBoundary>
                  </IntelRoute>
                }
              />
              <Route 
                path="/intelligence/booking-funnel"
                element={
                  <IntelRoute tab="booking-funnel">
                    <ErrorBoundary name="BookingFunnelReport">
                      <BookingFunnelReport />
                    </ErrorBoundary>
                  </IntelRoute>
                } 
              />
              <Route 
                path="/intelligence/website-analytics"
                element={
                  <IntelRoute tab="website-analytics">
                    <ErrorBoundary name="WebsiteAnalyticsReport">
                      <WebsiteAnalyticsReport />
                    </ErrorBoundary>
                  </IntelRoute>
                } 
              />
              <Route 
                path="/intelligence/audiences"
                element={
                  <IntelRoute tab="audiences">
                    <ErrorBoundary name="AudiencesReport">
                      <AudiencesReport />
                    </ErrorBoundary>
                  </IntelRoute>
                } 
              />
              <Route 
                path="/intelligence/campaigns"
                element={
                  <IntelRoute tab="campaigns">
                    <ErrorBoundary name="CampaignsReport">
                      <CampaignsReport />
                    </ErrorBoundary>
                  </IntelRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute requiredType="admin">
                    <ProfilePage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/org-settings" 
                element={
                  <ProtectedRoute requiredType="admin">
                    <OrgSettingsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/teamMember-view" 
                element={
                  <ProtectedRoute requiredType="teamMember">
                    <TeamMemberViewV2 />
                  </ProtectedRoute>
                } 
              />
              
              {/* Super Admin route */}
              <Route 
                path="/super-admin" 
                element={
                  <ProtectedRoute requiredType="super_admin">
                    <SuperAdminDashboardV2 />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/super-admin-legacy" 
                element={
                  <ProtectedRoute requiredType="super_admin">
                    <SuperAdminDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Partner Portal - public access with token */}
              <Route path="/partner-portal" element={<ErrorBoundary name="PartnerPortal"><PartnerPortalPage /></ErrorBoundary>} />
              <Route path="/partner-portal/:partnerId" element={<ErrorBoundary name="PartnerPortal"><PartnerPortalPage /></ErrorBoundary>} />
              
              {/* Redirect old routes */}
              <Route path="/teamMember-login" element={<Navigate to="/login" replace />} />
              <Route path="/partners" element={<Navigate to="/admin?tab=partners" replace />} />
              
              {/* 404 Catch-all - must be last */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
            {/* Cookie consent — marketing pages only (hidden inside the app), web only */}
            {!isNativeApp && <CookieConsent />}
          </BrowserRouter>
          <Toaster />
        </div>
        </CustomerAuthProvider>
      </SettingsProvider>
    </HelmetProvider>
    // </ClerkProvider>
  );
}

export default App;